import {
  Input,
  Box,
  Text,
  VStack,
  HStack,
  Stack,
  Divider,
} from "@chakra-ui/react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Select } from "chakra-react-select";
import WhiteBgInput from "./WhiteBgInput";
import CampaignTypeCard from "./components/CampaignTypeCard";
import { StepProps } from "./types";
import { KYDRadioGroup } from "src/ui";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { CampaignType } from "common/interfaces/KYDMarketing";
import BudgetSelector from "./components/BudgetSelector";
import campaignTransform, { CampaignFormValues } from "./transform/campaign";
import { useAudiences } from "./hooks/useAudiences";
import AudienceCard from "./components/AudienceCard";
import WarningBox from "./components/WarningBox";

const CampaignForm = forwardRef<
  ReturnType<typeof useForm<CampaignFormValues>>,
  StepProps
>(
  (
    {
      adAccounts = [],
      campaign,
      loading,
      back,
      data,
      instagramAccounts,
      pages,
      events,
    },
    ref
  ) => {
    const { state: audiencesState, actions: audiencesActions } = useAudiences();
    const isEdit = !!campaign?.id;
    const formInput = campaignTransform.input({
      campaign,
      adAccounts,
    });
    const form = useForm<CampaignFormValues>({
      // Create mode: use defaultValues so the user's event selection isn't overwritten on re-renders.
      // Edit mode: use values to keep form in sync with the campaign.
      ...(isEdit ? { values: formInput } : { defaultValues: formInput }),
    });
    const { register, control, watch, setValue } = form;
    // Expose form methods to parent component through ref
    useImperativeHandle(ref, () => form, [form]);

    const ad_account_id = watch("ad_account.value");

    useEffect(() => {
      if (ad_account_id) {
        audiencesActions.list({
          ad_account_id: ad_account_id,
          next: false,
        });
      }
    }, [audiencesActions.list, ad_account_id]);

    return (
      <FormProvider {...form}>
        <VStack w="full" p={[2, 4]} px={[4, 8]}>
          <Stack w="full" spacing={4} direction={["column"]}>
            <VStack w="full" h="full" spacing={2}>
              <Text
                w="full"
                fontSize={"xl"}
                fontWeight={"bold"}
                textAlign={"left"}
              >
                Campaign setup
              </Text>
              {!isEdit && events && events.length > 0 ? (
                <>
                  <Text w="full" fontWeight={"bold"} textAlign={"left"}>
                    Event (*)
                  </Text>
                  <Controller
                    control={control}
                    name="event_ids"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        isDisabled={loading.campaigns}
                        isMulti
                        placeholder="Select events"
                        value={(field.value || []).map((value: string) => ({
                          value,
                          label:
                            events.find((e) => e.id === value)?.name || value,
                        }))}
                        onChange={(options) =>
                          field.onChange(
                            Array.isArray(options)
                              ? options.map((option) => option.value)
                              : []
                          )
                        }
                        chakraStyles={{
                          container: () => ({
                            position: "relative",
                            width: "100%",
                            backgroundColor: "white",
                          }),
                        }}
                        options={events.map((e) => ({
                          value: e.id,
                          label: e.name,
                        }))}
                      />
                    )}
                  />
                </>
              ) : null}
              <Text w="full" fontWeight={"bold"} textAlign={"left"}>
                Ad Account (*)
              </Text>
              {isEdit ? (
                <WhiteBgInput
                  isDisabled
                  placeholder="Ad Account"
                  value={`${
                    adAccounts?.find((a) => a.id === campaign?.account_id)?.name
                  } (${campaign?.account_id})`}
                />
              ) : (
                <Controller
                  control={control}
                  name="ad_account"
                  disabled={isEdit}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isDisabled={loading.campaigns || loading.adaccounts}
                      value={field.value}
                      defaultValue={field.value}
                      onChange={(values) => field.onChange(values)}
                      chakraStyles={{
                        container: () => ({
                          position: "relative",
                          width: "100%",
                          backgroundColor: "white",
                        }),
                      }}
                      // @ts-ignore
                      options={adAccounts?.map((a) => ({
                        label: `${a.name} (${a.id})`,
                        value: a.id,
                      }))}
                    />
                  )}
                />
              )}
              {!adAccounts.length ? (
                <WarningBox
                  title="No ad accounts found"
                  description="You need to connect an ad account to KYD to create a campaign."
                />
              ) : null}
              <Text
                w="full"
                fontSize={"large"}
                fontWeight={"bold"}
                textAlign={"left"}
              >
                Campaign Name (*)
              </Text>
              <Box w="full">
                <WhiteBgInput
                  isDisabled={loading.campaigns}
                  placeholder="Name"
                  {...register(`name`, { required: true })}
                />
              </Box>
              <Box w="full">
                <Text fontSize={"xs"} color="gray.400" w="full">
                  This name will be used to identify the campaign in the
                  Facebook Ads Manager
                </Text>
              </Box>
            </VStack>
            <Divider borderColor="gray.200" />
            <VStack h="full" spacing={4}>
              <VStack w="full" h="full" spacing={2}>
                <Text
                  w="full"
                  fontSize={"xl"}
                  fontWeight={"bold"}
                  textAlign={"left"}
                >
                  Campaign Objective
                </Text>
                <Text
                  fontSize={"xs"}
                  textAlign={"left"}
                  w="full"
                  color="gray.400"
                >
                  Select the type of campaign that best matches your goals. This
                  will determine how Meta optimizes your ad delivery.
                </Text>
              </VStack>
              <HStack
                gap={2}
                opacity={loading.campaigns || isEdit ? 0.5 : 1}
                pointerEvents={loading.campaigns || isEdit ? "none" : "auto"}
              >
                <CampaignTypeCard
                  type="conversion"
                  title="Sales Campaign"
                  description="Sales Campaign is a campaign that will be used to create ticket sales"
                  isSelected={watch(`campaign_type`) === "conversion"}
                  isDisabled={loading.campaigns || isEdit}
                  onClick={() =>
                    !isEdit
                      ? setValue(`campaign_type`, CampaignType.CONVERSION)
                      : undefined
                  }
                  badges={["Recommended", "Recommended"]}
                />
                <CampaignTypeCard
                  type="awareness"
                  title="Awareness Campaign"
                  description="Awareness Campaign is a campaign that will be used to increase the visibility of your event"
                  isSelected={watch(`campaign_type`) === "awareness"}
                  isDisabled={loading.campaigns || isEdit}
                  onClick={() =>
                    !isEdit
                      ? setValue(`campaign_type`, CampaignType.AWARENESS)
                      : undefined
                  }
                  badges={["Recommended", "Recommended"]}
                />
              </HStack>
            </VStack>
            <Divider borderColor="gray.200" />

            <VStack w="full" h="full" spacing={2}>
              <Text
                w="full"
                fontSize={"xl"}
                fontWeight={"bold"}
                textAlign={"left"}
              >
                Budget strategy
              </Text>
            </VStack>
            <KYDRadioGroup
              name="budget_type"
              value={watch(`budget_type`)}
              options={[
                {
                  title: "Campaign budget",
                  description:
                    "Automatically distribute your budget to the best opportunities across your campaign. \nAlso known as Advantage+ campaign budget.",
                  value: "campaign",
                  content: (
                    <BudgetSelector
                      value={watch("budget")}
                      onChange={(budget) => {
                        setValue(
                          "budget",
                          {
                            type: (budget.type || "daily") as
                              | "daily"
                              | "lifetime",
                            amount: {
                              amount: budget.amount?.amount || 0,
                              currency: budget.amount?.currency || "USD",
                            },
                          },
                          {
                            shouldDirty: true,
                            shouldValidate: true,
                          }
                        );
                      }}
                      currency={"USD"}
                      // disabled={loading.campaigns || isEdit}
                    />
                  ),
                },
                {
                  title: "Ad set budget",
                  description:
                    "Set different bid strategies or budget schedules for each ad set.",
                  value: "adset",
                },
              ]}
              onChange={(value: "campaign" | "adset") => {
                setValue(`budget_type`, value);

                if (value === "adset") {
                  setValue("budget", undefined);
                }
              }}
            />
            <Divider borderColor="gray.200" />
            <Text
              w="full"
              fontSize={"xl"}
              fontWeight={"bold"}
              textAlign={"left"}
            >
              Audience
            </Text>
            <AudienceCard
              fieldName="custom_audiences"
              instagramAccounts={instagramAccounts}
              pages={pages}
              loading={{
                campaigns: loading.campaigns,
                audiences: audiencesState.loading.audiences,
              }}
              isEdit={isEdit}
              ad_account_id={ad_account_id}
              audiences={watch("custom_audiences")}
            />
          </Stack>
        </VStack>
      </FormProvider>
    );
  }
);

export default CampaignForm;
